import { ClientCombobox } from "@/components/ClientCombobox";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, euro, fmtDate } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { CopyCostContracts } from "@/components/CopyCostContracts";
import { StatusBadge, TypeBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PlusIcon as Plus, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, ClockIcon as Clock, CalendarBlankIcon as CalendarBlank, BuildingsIcon as Buildings, PrinterIcon as Printer, WrenchIcon as Wrench, ShieldCheckIcon as ShieldCheck, LightningIcon as Lightning, GearIcon as Gear, WarningCircleIcon as WarningCircle, MagnifyingGlassIcon as MagnifyingGlass, CaretDownIcon as CaretDown, CaretRightIcon as CaretRight } from "@phosphor-icons/react";

const UNCAT = "__uncat__";
const CAT_ICONS = { Wrench, Lightning, Gear, ShieldCheck, Printer, FileText: Buildings };

// dettaglio helper
const d = (c, k) => (c.dettagli || {})[k];

export default function Contracts() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const ccp = { device_id: sp.get("device_id"), cliente_id: sp.get("cliente_id"), cc_new: sp.get("cc_new") };
  const [contracts, setContracts] = useState(null);
  const [clients, setClients] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [fCliente, setFCliente] = useState("all");
  const [fStato, setFStato] = useState("all");
  const [q, setQ] = useState("");
  const [openRows, setOpenRows] = useState({});
  const [sortAZ, setSortAZ] = useState(true);
  const [fAlert, setFAlert] = useState(null);

  const changeTab = (slug) => {
    setActiveTab(slug); setQ(""); setFAlert(null);
    const next = new URLSearchParams(sp); next.set("tab", slug); setSp(next, { replace: true });
  };

  const load = useCallback(() => {
    const params = {};
    if (fCliente !== "all") params.cliente_id = fCliente;
    api.get("/contracts", { params }).then(({ data }) => setContracts(data));
  }, [fCliente]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const a = sp.get("alert"); if (a) setFAlert(a); }, []);
  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/contratti-config").then(({ data }) => {
      const cats = (data.categorie || []).filter((c) => c.attiva).sort((a, b) => a.ordine - b.ordine);
      setCategorie(cats);
      if (cats.length) setActiveTab((prev) => prev || sp.get("tab") || cats[0].slug);
    });
  }, []);

  const catSlugs = categorie.map((c) => c.slug);
  const isUncat = (c) => { const cat = c.categoria || ""; return !cat || !catSlugs.includes(cat); };
  const countFor = (slug) => (contracts || []).filter((c) => (slug === UNCAT ? isUncat(c) : slug === "noleggio_costo_copia" ? (c.categoria === slug && c.copy_cost === true) : c.categoria === slug)).length;
  const matchesQ = (c) => {
    const term = q.trim().toLowerCase();
    if (!term) return true;
    const hay = [c.nome, c.cliente_nome, c.tipo, c.stato, c.note, c.numero, c.codice, c.codice_contratto,
      ...Object.values(c.dettagli || {})]
      .filter((v) => typeof v === "string" || typeof v === "number")
      .join(" ").toLowerCase();
    return hay.includes(term);
  };
  const assistMetrics = (c) => {
    const val = c.valorizzazione; const cons = c.consumo || {};
    const tot = val ? val.ore_caricate : (cons.ore_acquistate ?? (Number(c.ore_acquistate) || 0));
    const residue = val ? val.ore_residue : (cons.ore_residue ?? tot);
    const neg = val ? !!val.saldo_negativo : !!cons.saldo_negativo;
    const soglia = Number((c.dettagli || {}).soglia_avviso_ore) || (tot * 0.2);
    const poche = !neg && residue > 0 && residue <= soglia;
    const rinnovo = (c.stato || "") === "da_rinnovare";
    return { residue, neg, poche, rinnovo };
  };
  const assistRows = (contracts || []).filter((c) => c.categoria === "assistenza");
  const assistStats = {
    rinnovo: assistRows.filter((c) => assistMetrics(c).rinnovo).length,
    poche: assistRows.filter((c) => assistMetrics(c).poche).length,
    negativo: assistRows.filter((c) => assistMetrics(c).neg).length,
  };

  const visible = (() => {
    let rows = (contracts || [])
      .filter((c) => (activeTab === UNCAT ? isUncat(c) : c.categoria === activeTab))
      .filter((c) => fStato === "all" || (c.stato || "") === fStato)
      .filter((c) => {
        if (activeTab !== "assistenza" || !fAlert) return true;
        const m = assistMetrics(c);
        return fAlert === "rinnovo" ? m.rinnovo : fAlert === "poche" ? m.poche : fAlert === "negativo" ? m.neg : true;
      })
      .filter(matchesQ);
    if (sortAZ) rows = [...rows].sort((a, b) => (a.cliente_nome || "").localeCompare(b.cliente_nome || "", "it", { sensitivity: "base" }) || (a.nome || "").localeCompare(b.nome || "", "it", { sensitivity: "base" }));
    return rows;
  })();

  const openNew = () => {
    const cat = activeTab && activeTab !== UNCAT ? activeTab : "";
    nav(`/contratti/nuovo${cat ? `?categoria=${cat}` : ""}`);
  };

  const CatIcon = ({ icona, ...p }) => { const I = CAT_ICONS[icona] || Buildings; return <I {...p} />; };

  // riepilogo card per categoria (usa i dati consumo calcolati dal backend)
  const CardSummary = ({ c }) => {
    const cat = c.categoria;
    const cons = c.consumo || {};
    if (cat === "assistenza") {
      const val = c.valorizzazione;
      const tot = val ? val.ore_caricate : (cons.ore_acquistate ?? (Number(c.ore_acquistate) || 0));
      const usate = val ? val.ore_usate : (cons.ore_usate ?? 0);
      const residue = val ? val.ore_residue : (cons.ore_residue ?? tot);
      const neg = val ? val.saldo_negativo : !!cons.saldo_negativo;
      const pct = tot > 0 ? Math.min(100, Math.round((usate / tot) * 100)) : (usate > 0 ? 100 : 0);
      return (
        <div className="border-t border-border pt-3 space-y-2" data-testid="card-consumo-assistenza">
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">Ore usate</span><span className="tabular-nums font-medium">{usate}h / {tot}h</span></div>
          <div className="h-1.5 rounded-full bg-secondary overflow-hidden"><div className={`h-full ${neg ? "bg-rose-500" : pct >= 90 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} /></div>
          {neg ? (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600" data-testid="alert-saldo-negativo"><WarningCircle size={13} weight="fill" /> Saldo negativo: {residue}h{val && val.ore_negative > 0 ? ` (-${val.ore_negative}h)` : ""}</div>
          ) : (
            <div className="flex items-center gap-1.5 text-xs font-medium text-primary"><Clock size={13} weight="bold" /> {residue}h residue</div>
          )}
          {val && (
            <div className="grid grid-cols-3 gap-1.5 pt-1 text-[11px]" data-testid="card-valorizzazione">
              <div><div className="text-muted-foreground">Val. caricato</div><div className="font-semibold tabular-nums">{euro(val.valore_caricato)}</div></div>
              <div><div className="text-muted-foreground">Val. maturato</div><div className="font-semibold tabular-nums text-amber-600">{euro(val.valore_maturato)}</div></div>
              <div><div className="text-muted-foreground">Residuo/risconto</div><div className="font-semibold tabular-nums text-emerald-600">{euro(val.valore_residuo)}</div></div>
            </div>
          )}
          {val && val.valore_da_valorizzare_note && <div className="text-[11px] text-amber-700">{val.valore_da_valorizzare_note}</div>}
        </div>
      );
    }
    if (cat === "flat") {
      const ore = cons.ore_valide ?? cons.ore_periodo ?? 0;
      const maxore = cons.monte_ore_massimo ?? (Number(d(c, "monte_ore_massimo")) || 0);
      const superato = !!cons.superato;
      const tar = cons.tariffa_oraria_effettiva;
      const ricavo = cons.ricavo_flat ?? cons.importo_periodo ?? (Number(c.importo) || 0);
      const valRend = cons.valore_rendicontato ?? 0;
      const tipol = cons.tipologia === "a_progetto" ? "A progetto" : "Mensile";
      const pct = maxore > 0 ? Math.min(100, Math.round((ore / maxore) * 100)) : 0;
      return (
        <div className="border-t border-border pt-3 space-y-2" data-testid="card-consumo-flat">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Tipologia</span>
            <span className="font-medium">{tipol}</span>
          </div>
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">Ricavo FLAT</span><span className="tabular-nums font-medium">{euro(ricavo)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">Ore</span><span className="tabular-nums font-medium">{ore}h{maxore > 0 ? ` / ${maxore}h` : ""}</span></div>
          {maxore > 0 && <div className="h-1.5 rounded-full bg-secondary overflow-hidden"><div className={`h-full ${superato ? "bg-rose-500" : pct >= 90 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} /></div>}
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Tariffa effettiva</span>
            <span className="tabular-nums font-medium">{tar != null ? `${euro(tar)}/h` : "—"}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Valore rendicontato</span>
            <span className="tabular-nums font-semibold text-amber-600">{euro(valRend)}</span>
          </div>
          {superato && <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600" data-testid="alert-flat-superato"><WarningCircle size={13} weight="fill" /> Superamento monte ore: +{cons.ore_superamento}h</div>}
        </div>
      );
    }
    if (cat === "manutenzione") {
      const eseg = cons.interventi_eseguiti ?? (Number(d(c, "interventi_eseguiti")) || 0);
      const incl = cons.interventi_inclusi ?? (Number(d(c, "numero_interventi_inclusi")) || 0);
      const residui = cons.interventi_residui;
      const superato = !!cons.superato;
      const pct = incl > 0 ? Math.min(100, Math.round((eseg / incl) * 100)) : 0;
      return (
        <div className="border-t border-border pt-3 space-y-2" data-testid="card-consumo-manutenzione">
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">Interventi</span><span className="tabular-nums font-medium">{eseg}{incl > 0 ? ` / ${incl}` : ""}</span></div>
          {incl > 0 && <div className="h-1.5 rounded-full bg-secondary overflow-hidden"><div className={`h-full ${superato ? "bg-rose-500" : pct >= 90 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} /></div>}
          {superato ? (
            <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600" data-testid="alert-manut-superato"><WarningCircle size={13} weight="fill" /> Interventi inclusi superati</div>
          ) : residui != null ? (
            <div className="flex items-center gap-1.5 text-xs font-medium text-primary"><CheckCircle size={13} weight="bold" /> {residui} residui</div>
          ) : (
            <div className="text-xs text-muted-foreground">Prossima manut.: {fmtDate(d(c, "prossima_manutenzione")) || "—"}</div>
          )}
        </div>
      );
    }
    if (cat === "amministratore_sistema") {
      return (
        <div className="border-t border-border pt-3 grid grid-cols-2 gap-2 text-sm">
          <Field label="Fine incarico" value={fmtDate(d(c, "data_fine_incarico")) || "—"} />
          <Field label="Prossima verifica" value={fmtDate(d(c, "prossima_verifica")) || "—"} />
          {d(c, "referente_privacy") && <div className="col-span-2"><Field label="DPO / privacy" value={d(c, "referente_privacy")} /></div>}
        </div>
      );
    }
    if (cat === "noleggio_costo_copia") {
      return (
        <div className="border-t border-border pt-3 grid grid-cols-2 gap-2 text-sm">
          <Field label="Modello" value={d(c, "modello_fotocopiatrice") || "—"} />
          <Field label="Matricola" value={d(c, "matricola") || "—"} />
          <Field label="Canone" value={d(c, "canone_noleggio") ? euro(d(c, "canone_noleggio")) : "—"} />
          <Field label="Scadenza" value={fmtDate(d(c, "data_scadenza_contratto") || c.data_scadenza) || "—"} />
        </div>
      );
    }
    return (
      <div className="border-t border-border pt-3 grid grid-cols-2 gap-2 text-sm">
        <Field label="Importo periodo" value={c.importo ? euro(c.importo) : "—"} />
        <Field label="Scadenza" value={fmtDate(c.data_scadenza) || "—"} />
      </div>
    );
  };

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader
          title="Contratti"
          subtitle="Contratti a canone e a monte ore, organizzati per categoria"
          actions={
            <Button data-testid="new-contract-btn" onClick={openNew} className="rounded-sm gap-2">
              <Plus size={16} weight="bold" /> Nuovo contratto
            </Button>
          }
        />

        <div className="flex flex-wrap items-center gap-1.5 border-b border-border pb-2 mb-5" data-testid="contract-tabs">
          {categorie.map((cat) => (
            <button key={cat.slug} onClick={() => changeTab(cat.slug)} data-testid={`contract-tab-${cat.slug}`}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-[13px] whitespace-nowrap border transition-colors ${activeTab === cat.slug ? "border-primary bg-primary/5 text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
              <CatIcon icona={cat.icona} size={14} weight={activeTab === cat.slug ? "fill" : "regular"} />
              {cat.nome}
              <span className="text-[11px] tabular-nums rounded-full bg-secondary px-1.5 py-0.5">{countFor(cat.slug)}</span>
            </button>
          ))}
          <button onClick={() => changeTab(UNCAT)} data-testid="contract-tab-uncat"
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-[13px] whitespace-nowrap border transition-colors ${activeTab === UNCAT ? "border-amber-500 bg-amber-50 text-amber-600 font-semibold" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
            <XCircle size={14} weight={activeTab === UNCAT ? "fill" : "regular"} />
            Non categorizzati
            <span className="text-[11px] tabular-nums rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5">{countFor(UNCAT)}</span>
          </button>
        </div>

        {activeTab === "noleggio_costo_copia" ? (
          <CopyCostContracts presetDeviceId={ccp.device_id} presetClientId={ccp.cliente_id} autoNew={ccp.cc_new === "1"} onDone={load} />
        ) : (<>
        {activeTab === "assistenza" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="assistenza-kpi-cards">
            {[
              { key: "rinnovo", label: "Da rinnovare", value: assistStats.rinnovo, Icon: CalendarBlank, color: "amber", hint: "Contratti scaduti o da rinnovare" },
              { key: "poche", label: "Poche ore residue", value: assistStats.poche, Icon: Clock, color: "orange", hint: "Monte ore sotto la soglia di avviso" },
              { key: "negativo", label: "Monte ore negativo", value: assistStats.negativo, Icon: WarningCircle, color: "rose", hint: "Saldo ore in sforamento" },
            ].map(({ key, label, value, Icon, color, hint }) => {
              const active = fAlert === key;
              const palette = {
                amber: active ? "border-amber-500 bg-amber-50" : "hover:border-amber-300",
                orange: active ? "border-orange-500 bg-orange-50" : "hover:border-orange-300",
                rose: active ? "border-rose-500 bg-rose-50" : "hover:border-rose-300",
              }[color];
              const icon = { amber: "text-amber-500", orange: "text-orange-500", rose: "text-rose-500" }[color];
              return (
                <button key={key} type="button" onClick={() => setFAlert((v) => (v === key ? null : key))} data-testid={`assistenza-kpi-${key}`}
                  className={`text-left rounded-sm border bg-white p-4 transition-colors ${palette}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Icon size={15} weight="fill" className={icon} /> {label}</div>
                    <span className="text-2xl font-heading font-bold tabular-nums" data-testid={`assistenza-kpi-${key}-count`}>{value}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1.5">{hint}{active ? " · filtro attivo" : ""}</div>
                </button>
              );
            })}
          </div>
        )}
        <div className="flex flex-wrap gap-3 mb-6">
          <ClientCombobox clients={clients} value={fCliente} width="w-64" onChange={setFCliente} extraOptions={[{ value: "all", label: "Tutti i clienti" }]} placeholder="Cliente" testid="filter-cliente" />
          <Select value={fStato} onValueChange={setFStato}>
            <SelectTrigger className="w-48 rounded-sm bg-white" data-testid="filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="attivo">Attivo</SelectItem>
              <SelectItem value="da_rinnovare">Da rinnovare</SelectItem>
              <SelectItem value="sospeso">Sospeso</SelectItem>
              <SelectItem value="cessato">Cessato</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[16rem] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per cliente, nome, tipo…" className="pl-9 rounded-sm bg-white" data-testid="contract-search" />
          </div>
          <Button variant={sortAZ ? "default" : "outline"} className="rounded-sm gap-1" onClick={() => setSortAZ((v) => !v)} data-testid="contract-sort-az">
            <CaretDown size={14} /> {sortAZ ? "Ordine A-Z attivo" : "Ordina A-Z"}
          </Button>
        </div>

        {contracts === null ? (
          <div className="text-muted-foreground py-16 text-center">Caricamento…</div>
        ) : visible.length === 0 ? (
          <div className="text-muted-foreground py-16 text-center border border-dashed border-border rounded-sm" data-testid="contracts-empty">
            Nessun contratto in questa categoria.
          </div>
        ) : (
          <div className="space-y-2" data-testid="contracts-list">
            {visible.map((c) => {
              const open = !!openRows[c.id];
              return (
                <Card key={c.id} data-testid="contract-row" className="rounded-sm border-border shadow-none overflow-hidden">
                  <button type="button" onClick={() => setOpenRows((o) => ({ ...o, [c.id]: !o[c.id] }))} data-testid="contract-row-toggle"
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-secondary/50 transition-colors">
                    {open ? <CaretDown size={16} className="text-muted-foreground shrink-0" /> : <CaretRight size={16} className="text-muted-foreground shrink-0" />}
                    <Buildings size={16} className="text-primary shrink-0" />
                    <span className="font-heading font-semibold truncate flex-1" data-testid="contract-row-cliente">{c.cliente_nome || "—"}</span>
                    <StatusBadge status={c.stato} />
                  </button>
                  {open && (
                    <div className="border-t border-border p-4 space-y-3 bg-secondary/20" data-testid="contract-row-detail">
                      <div className="flex items-center gap-2 flex-wrap"><TypeBadge tipo={c.tipo} /><span className="text-sm font-medium">{c.nome}</span></div>
                      <CardSummary c={c} />
                      <div className="flex items-center justify-end border-t border-border pt-3 text-xs">
                        <span className="text-muted-foreground inline-flex items-center gap-1"><CalendarBlank size={12} /> {fmtDate(c.data_scadenza) || "—"}</span>
                      </div>
                      <div className="flex justify-end">
                        <Button size="sm" className="rounded-sm gap-1" onClick={() => nav(`/contratti/${c.id}`)} data-testid="contract-open-btn"><MagnifyingGlass size={14} /> Apri contratto</Button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
        </>)}
      </div>
    </InternalLayout>
  );
}

const Field = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-wider text-muted-foreground truncate">{label}</div>
    <div className="font-medium truncate">{value}</div>
  </div>
);
